'use client';

import { useEffect, useState, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useLocale } from 'next-intl';
import {
  Box,
  Container,
  Typography,
  Button,
  Alert,
  Paper,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import { signIn, signOut } from 'next-auth/react';
import { motion } from 'framer-motion';
import { getAndClearReturnUrl } from '@/lib/api/client';
import { getUiCopy } from '@/i18n/uiCopy';

/**
 * Mengir Login Page with Federated Identity Providers
 * Supports:
 * - Google (via Cognito federated identity)
 * - Email (Cognito native)
 */

// Cognito federated identity provider identifiers
const IDP_GOOGLE = process.env.NEXT_PUBLIC_COGNITO_IDP_GOOGLE || 'Google';

const isSafeRelativeUrl = (value: string | null): boolean => {
  if (!value) return false;
  if (!value.startsWith('/')) return false;
  return !value.startsWith('//');
};

type LoginContentProps = {
  sessionError?: string | null;
};

function LoginContent({ sessionError }: LoginContentProps) {
  const searchParams = useSearchParams();
  const theme = useTheme();
  const locale = useLocale();
  const copy = getUiCopy(locale);
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [callbackUrl, setCallbackUrl] = useState('/listings');

  const isDark = theme.palette.mode === 'dark';
  const returnToParam =
    searchParams?.get('returnTo') || searchParams?.get('callbackUrl');

  // Determine callback URL: URL param > stored return URL > default
  useEffect(() => {
    if (isSafeRelativeUrl(returnToParam)) {
      setCallbackUrl(returnToParam!);
    } else {
      // Check for stored return URL from session expiry redirect
      const storedUrl = getAndClearReturnUrl();
      if (storedUrl && isSafeRelativeUrl(storedUrl)) {
        setCallbackUrl(storedUrl);
      }
    }
  }, [returnToParam]);
  const errorParam = searchParams?.get('error');
  const pageMode =
    searchParams?.get('mode') === 'signup' ? 'signup' : 'signin';
  const isSignup = pageMode === 'signup';
  const toggleMode = isSignup ? 'signin' : 'signup';
  const toggleHref = isSafeRelativeUrl(returnToParam)
    ? `/auth/login?mode=${toggleMode}&returnTo=${encodeURIComponent(
        returnToParam!
      )}`
    : `/auth/login?mode=${toggleMode}`;

  useEffect(() => {
    if (!sessionError) return;
    setError(copy.login.errors.sessionExpired);
    void signOut({ redirect: false });
  }, [copy.login.errors.sessionExpired, sessionError]);

  // Parse error from URL
  useEffect(() => {
    if (errorParam) {
      const errorMessages: Record<string, string> = {
        Configuration: copy.login.errors.configuration,
        AccessDenied: copy.login.errors.accessDenied,
        Verification: copy.login.errors.verification,
        OAuthSignin: copy.login.errors.oauthSignin,
        OAuthCallback: copy.login.errors.oauthCallback,
        OAuthCreateAccount: copy.login.errors.oauthCreateAccount,
        Callback: copy.login.errors.callback,
        OAuthAccountNotLinked: copy.login.errors.oauthAccountNotLinked,
        SessionRequired: copy.login.errors.sessionRequired,
        SessionExpired: copy.login.errors.sessionExpired,
        default: copy.login.errors.default,
      };
      setError(errorMessages[errorParam] || errorMessages.default);
    }
  }, [copy.login.errors, errorParam]);

  const handleAuth = async (
    provider: 'google' | 'email',
    mode: 'signin' | 'signup' = 'signin'
  ) => {
    setIsLoading(provider);
    setError(null);

    const authorizationParams: Record<string, string> = {};

    // Add screen_hint for signup mode
    if (mode === 'signup') {
      authorizationParams['screen_hint'] = 'signup';
    }

    // Add identity_provider for federated identities
    if (provider === 'google') {
      authorizationParams['identity_provider'] = IDP_GOOGLE;
    }

    try {
      await signIn(
        'cognito',
        { callbackUrl, redirect: true },
        authorizationParams
      );
    } catch (err) {
      console.error('[login] Auth failed:', err);
      setError(copy.login.errors.oauthCallback);
      setIsLoading(null);
    }
  };

  return (
    <Box className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Animated gradient orbs */}
      <motion.div
        className="absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl"
        style={{
          background: isDark
            ? 'linear-gradient(135deg, #a78bfa 0%, #4ade80 100%)'
            : 'linear-gradient(135deg, #2563eb 0%, #16a34a 100%)',
          opacity: 0.2,
        }}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.2, 0.3, 0.2],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      <motion.div
        className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full blur-3xl"
        style={{
          background: isDark
            ? 'linear-gradient(135deg, #4ade80 0%, #a78bfa 100%)'
            : 'linear-gradient(135deg, #16a34a 0%, #2563eb 100%)',
          opacity: 0.2,
        }}
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.3, 0.2, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Content */}
      <Container maxWidth="sm" className="relative z-10 py-8">
        {/* Back button */}
        <Button
          component={Link}
          href="/"
          startIcon={<ArrowBackIcon />}
          disabled={isLoading !== null}
          className="mb-8"
          sx={{
            textTransform: 'none',
            color: 'text.primary',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          {copy.login.backToHome}
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Login card */}
          <Paper
            elevation={0}
            className="glass"
            sx={{
              p: { xs: 3, sm: 5 },
              borderRadius: 3,
              border: '2px solid',
              borderColor: isDark ? 'primary.dark' : 'primary.light',
              backdropFilter: 'blur(20px)',
              background: isDark
                ? 'rgba(10, 10, 10, 0.95)'
                : 'rgba(255, 255, 255, 0.95)',
            }}
            data-cy="login-form"
          >
            {/* Header */}
            <Box className="text-center mb-8">
              <Typography
                variant="h3"
                className="mb-3"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2rem', sm: '2.5rem' },
                }}
                >
                  <Box
                  component="span"
                  sx={{
                    background: isDark
                      ? 'linear-gradient(135deg, #a78bfa 0%, #4ade80 50%, #a78bfa 100%)'
                      : 'linear-gradient(135deg, #2563eb 0%, #16a34a 50%, #2563eb 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundSize: '200% auto',
                  }}
                >
                  {isSignup ? copy.login.joinTitle : copy.login.welcomeTitle}
                </Box>
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {isSignup
                  ? copy.login.joinSubtitle
                  : copy.login.welcomeSubtitle}
              </Typography>
            </Box>

            {/* Error alert */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Alert
                  severity="error"
                  className="mb-6"
                  sx={{ borderRadius: 2 }}
                  onClose={() => setError(null)}
                  data-cy="login-error"
                >
                  {error}
                </Alert>
              </motion.div>
            )}

            {/* Auth buttons */}
            <Box className="flex flex-col gap-3 mb-6">
              {/* Google */}
              <Button
                aria-label={copy.login.continueWithGoogle}
                onClick={() => handleAuth('google', pageMode)}
                disabled={isLoading !== null}
                fullWidth
                size="large"
                disableElevation
                sx={(theme) => ({
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2.5,
                  py: 1.5,
                  color: '#3c4043',
                  backgroundColor: '#ffffff',
                  border: '2px solid',
                  borderColor:
                    theme.palette.mode === 'dark' ? '#5f6368' : '#dadce0',
                  '&:hover': {
                    backgroundColor:
                      theme.palette.mode === 'dark' ? '#f8f9fa' : '#f8f9fa',
                    borderColor:
                      theme.palette.mode === 'dark' ? '#5f6368' : '#c4c7c9',
                    transform: 'translateY(-2px)',
                  },
                  '&:disabled': {
                    backgroundColor: '#f1f3f4',
                    borderColor: '#e0e0e0',
                  },
                  transition: 'all 0.2s ease',
                })}
                startIcon={
                  isLoading === 'google' ? (
                    <Box className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Box
                      component="img"
                      alt="Google"
                      src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                      sx={{ width: 20, height: 20 }}
                    />
                  )
                }
              >
                {copy.login.continueWithGoogle}
              </Button>

              {/* Email */}
              <Button
                aria-label={copy.login.continueWithEmail}
                onClick={() => handleAuth('email', pageMode)}
                disabled={isLoading !== null}
                fullWidth
                size="large"
                variant="outlined"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2.5,
                  py: 1.5,
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s ease',
                }}
                startIcon={
                  isLoading === 'email' ? (
                    <Box className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <EmailIcon />
                  )
                }
              >
                {copy.login.continueWithEmail}
              </Button>
            </Box>

            <Box className="text-center mb-2">
              <Typography variant="body2" color="text.secondary">
                {isSignup ? copy.login.alreadyHaveAccount : copy.login.newToMengir}{' '}
                <Button
                  component={Link}
                  href={toggleHref}
                  size="small"
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                  disabled={isLoading !== null}
                >
                  {isSignup ? copy.login.signIn : copy.login.createAccount}
                </Button>
              </Typography>
            </Box>

            {/* Trust indicators */}
            <Box className="flex flex-wrap justify-center gap-4 pt-4 border-t border-divider">
              <Box className="flex items-center gap-1.5">
                <SecurityIcon
                  fontSize="small"
                  className="text-green-600 dark:text-green-400"
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                >
                  {copy.login.secure}
                </Typography>
              </Box>
              <Box className="flex items-center gap-1.5">
                <SpeedIcon
                  fontSize="small"
                  className="text-green-600 dark:text-green-400"
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                >
                  {copy.login.instantAccess}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Additional info */}
          <Typography
            variant="caption"
            color="text.secondary"
            className="block text-center mt-4"
          >
            {copy.login.termsIntro}{' '}
            <Link
              href="/terms"
              style={{
                color: theme.palette.primary.main,
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
              }}
            >
              {copy.login.termsOfService}
            </Link>
            {' '}and{' '}
            <Link
              href="/privacy"
              style={{
                color: theme.palette.primary.main,
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
              }}
            >
              {copy.login.privacyPolicy}
            </Link>
          </Typography>
        </motion.div>
      </Container>
    </Box>
  );
}

type LoginClientProps = {
  sessionError?: string | null;
};

export default function LoginClient({ sessionError }: LoginClientProps) {
  const locale = useLocale();
  const copy = getUiCopy(locale);

  return (
    <Suspense
      fallback={
        <Box className="min-h-screen flex items-center justify-center">
          <Box className="flex flex-col items-center gap-4">
            <CircularProgress size={48} />
            <Typography variant="body1" color="text.secondary">
              {copy.login.loading}
            </Typography>
          </Box>
        </Box>
      }
    >
      <LoginContent sessionError={sessionError} />
    </Suspense>
  );
}
